import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ShieldCheck } from "lucide-react";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";

export default function Wallet() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["wallet"], queryFn: () => api.get("/wallet/me").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(5000);
  const [processing, setProcessing] = useState(false);

  const handlePay = async () => {
    if (!amount || amount < 1) return toast.error("Enter a valid amount");
    setProcessing(true);
    try {
      await openRazorpayCheckout({
        amount,
        purpose: "wallet_topup",
        description: `FuelExpress wallet top-up ₹${amount}`,
        prefill: { name: user?.name || "", email: user?.email || "" },
        onSuccess: () => {
          setOpen(false);
          qc.invalidateQueries({ queryKey: ["wallet"] });
        },
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="wallet-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Wallet</h1>
        <p className="text-sm text-muted-foreground">Pre-pay your diesel orders. Instant deductions on delivery.</p>
      </div>

      <Card className="border-primary/40 bg-primary/5">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Current balance</div>
              <div className="mt-1 font-display text-5xl font-black text-primary" data-testid="wallet-balance">
                ₹{(data?.balance || 0).toLocaleString("en-IN")}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">Customer: {data?.customer_code}</div>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="lg" className="pill-btn" data-testid="btn-topup"><Plus size={16} className="mr-2" /> Top up</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Top up wallet</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Amount (₹)</Label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value))}
                      data-testid="topup-amount"
                      min={1}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[1000, 5000, 10000, 25000, 50000].map((a) => (
                      <button
                        key={a}
                        onClick={() => setAmount(a)}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                          amount === a ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"
                        }`}
                        data-testid={`topup-preset-${a}`}
                      >
                        ₹{a.toLocaleString("en-IN")}
                      </button>
                    ))}
                  </div>

                  <div className="rounded-lg border border-primary/30 bg-background p-3">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <ShieldCheck size={16} className="text-primary" />
                      Secure payment via Razorpay
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      All top-ups are processed through Razorpay&apos;s PCI-DSS certified checkout. Choose
                      from UPI, Cards, Net-Banking, or Wallets on the next screen. No amount is credited
                      until the payment is verified.
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handlePay}
                    disabled={!amount || amount < 1 || processing}
                    data-testid="btn-confirm-topup"
                    className="w-full"
                  >
                    {processing ? "Opening Razorpay…" : `Pay ₹${(amount || 0).toLocaleString("en-IN")} via Razorpay`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Recent transactions</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Date</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Method</th><th className="px-4 py-2 text-right">Amount</th></tr>
            </thead>
            <tbody>
              {(data?.transactions || []).map((t) => (
                <tr key={t.id} className="border-b border-border/40 text-sm">
                  <td className="px-4 py-2 font-mono">{t.code}</td>
                  <td className="px-4 py-2 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2 text-xs uppercase">{t.type}</td>
                  <td className="px-4 py-2 text-xs uppercase">{t.method}</td>
                  <td className={`px-4 py-2 text-right font-mono font-semibold ${t.type === "topup" ? "text-status-delivered" : "text-status-cancelled"}`}>{t.type === "topup" ? "+" : "-"}₹{t.amount.toLocaleString("en-IN")}</td>
                </tr>
              ))}
              {(!data?.transactions || data.transactions.length === 0) && (<tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">No transactions yet. Top up to get started.</td></tr>)}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
